import { baseUrl } from 'lib/baseUrl';
import {
  PING_TIMEOUT,
  PING_INTERVAL_NORMAL,
  PING_INTERVAL_DEGRADED,
  LATENCY_THRESHOLD_SLOW,
  CONSECUTIVE_FAILURES_UNSTABLE,
  SLOW_EFFECTIVE_TYPES,
  CONNECTION_STATES,
} from './constants';

class ConnectionQualityService {
  constructor() {
    this._timerId = null;
    this._callback = null;
    this._consecutiveFailures = 0;
    this._isMonitoring = false;
    this._handleVisibilityChange = this._handleVisibilityChange.bind(this);
  }

  async measureLatency() {
    const start = performance.now();
    // Hitting the configured API base URL forces real DNS + a
    // cross-origin request, which fails cleanly when the network is out.
    // mode: 'no-cors' sidesteps CORS preflight (we don't read the
    // response; any completed request means the server was reachable).
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), PING_TIMEOUT);
    try {
      await fetch(`${baseUrl}?_t=${Date.now()}`, {
        method: 'HEAD',
        cache: 'no-store',
        mode: 'no-cors',
        signal: controller.signal,
      });
      const latencyMs = Math.round(performance.now() - start);
      this._consecutiveFailures = 0;
      return { latencyMs, success: true };
    } catch {
      this._consecutiveFailures += 1;
      const latencyMs = Math.round(performance.now() - start);
      return { latencyMs, success: false };
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static getNetworkInfo() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return null;
    return {
      effectiveType: connection.effectiveType || null,
      downlink: connection.downlink ?? null,
      rtt: connection.rtt ?? null,
    };
  }

  classifyState(latencyResult, networkInfo) {
    if (!navigator.onLine) return CONNECTION_STATES.OFFLINE;

    if (!latencyResult.success) {
      return this._consecutiveFailures >= CONSECUTIVE_FAILURES_UNSTABLE
        ? CONNECTION_STATES.UNSTABLE
        : CONNECTION_STATES.ONLINE;
    }

    if (networkInfo && SLOW_EFFECTIVE_TYPES.includes(networkInfo.effectiveType)) {
      return CONNECTION_STATES.SLOW;
    }

    if (latencyResult.latencyMs > LATENCY_THRESHOLD_SLOW) {
      return CONNECTION_STATES.SLOW;
    }

    return CONNECTION_STATES.ONLINE;
  }

  _getInterval() {
    const isHealthy = this._consecutiveFailures === 0;
    return isHealthy ? PING_INTERVAL_NORMAL : PING_INTERVAL_DEGRADED;
  }

  async _tick() {
    if (!this._isMonitoring || document.hidden) return;

    const latencyResult = await this.measureLatency();
    const networkInfo = ConnectionQualityService.getNetworkInfo();
    const state = this.classifyState(latencyResult, networkInfo);

    if (this._callback) {
      this._callback({
        latencyMs: latencyResult.latencyMs,
        pingSuccess: latencyResult.success,
        networkInfo,
        state,
      });
    }

    this._scheduleNext();
  }

  _scheduleNext() {
    if (!this._isMonitoring) return;
    this._timerId = setTimeout(() => this._tick(), this._getInterval());
  }

  _handleVisibilityChange() {
    if (document.hidden) {
      clearTimeout(this._timerId);
      this._timerId = null;
    } else if (this._isMonitoring) {
      this._tick();
    }
  }

  startMonitoring(callback) {
    if (this._isMonitoring) return;
    this._callback = callback;
    this._isMonitoring = true;
    this._consecutiveFailures = 0;
    document.addEventListener('visibilitychange', this._handleVisibilityChange);
    this._tick();
  }

  stopMonitoring() {
    this._isMonitoring = false;
    this._callback = null;
    clearTimeout(this._timerId);
    this._timerId = null;
    document.removeEventListener('visibilitychange', this._handleVisibilityChange);
  }

  async checkNow() {
    const latencyResult = await this.measureLatency();
    const networkInfo = ConnectionQualityService.getNetworkInfo();
    const state = this.classifyState(latencyResult, networkInfo);
    return { latencyMs: latencyResult.latencyMs, pingSuccess: latencyResult.success, networkInfo, state };
  }
}

export default ConnectionQualityService;
